import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, ConfirmAction, SkeletonSet } from '@cwos/ui-admin';
import { directory, programs as programsApi, sites as sitesApi, users } from '@cwos/api-client';
import { LoadFailure } from '../LoadFailure.js';
import {
  escalation,
  type EscalationConfigLevel,
  type EscalationConfigScopeType,
  type EscalationPolicyKind,
} from '../insights/escalationClient.js';
import { targetConfigSentence } from '../insights/escalationLogic.js';

/**
 * ===========================================================================
 * ESCALATION TARGETS — rungs 1 and 4 of the ladder, and the constant that had
 * a home and no door.
 * ===========================================================================
 *
 * `finding_escalation_target_config` has existed since migration
 * `1854000000000` with exactly the right schema, and its two write routes had
 * no client anywhere. Ops Console's `ESCALATION_NAMES = ['lacey', 'azak',
 * 'abdirahman']` — three lowercase substrings matched against a spreadsheet —
 * has a precise structural home here, and until this screen existed a customer
 * could not put them in it.
 *
 * ---------------------------------------------------------------------------
 * TWO LEVELS, AND THEY ARE NOT A PRIORITY SETTING
 * ---------------------------------------------------------------------------
 *   policy        an explicit target for one finding type or one detector.
 *                 Rung 1 — consulted before anything else.
 *   org_fallback  the organization's fallback escalation ROLE. Rung 4 —
 *                 consulted only when every higher rung is silent.
 *
 * The ladder STOPS at the first rung that produces anybody, which is why a
 * policy target plus a program manager is not an ambiguity: a lower rung
 * answers only when every higher one said nothing. Unioning them would punish
 * an organization for configuring the thing the ruling asks it to configure.
 *
 * ---------------------------------------------------------------------------
 * WHERE AMBIGUITY ACTUALLY COMES FROM, SO THIS SCREEN CAN WARN ABOUT IT
 * ---------------------------------------------------------------------------
 * `program` and `location` rank THE SAME on the server, deliberately: a
 * finding carrying both, with a configuration on each, has two equally
 * specific answers and no principled reason to prefer either. That is a tie an
 * administrator can create here and would otherwise only discover later as an
 * "More than one escalation owner applies" in the queue — so the screen says
 * it while they are making it, rather than leaving them to find out.
 *
 * ---------------------------------------------------------------------------
 * THE SHAPE RULES ARE THE DATABASE'S, AND ARE NOT RE-TYPED HERE
 * ---------------------------------------------------------------------------
 * A fallback that names a person, a policy row with no policy key, two live
 * configurations for one policy and scope — all three are CHECK/UNIQUE
 * constraints, and the route turns 23514/23505 into a 400. This form disables
 * the obviously-wrong combinations so a person is not invited to fail, and
 * then SHOWS the server's refusal when one gets through. A second copy of the
 * rules here would be a second answer, and the database's is the one that
 * binds.
 *
 * ---------------------------------------------------------------------------
 * RETIRE, NEVER DELETE
 * ---------------------------------------------------------------------------
 * An escalation records the `configId` that chose its target. Removing the row
 * would make an already-routed escalation unexplainable — the same failure the
 * relationship snapshot exists to prevent one level down. So the control is
 * "Retire", the retired rows stay readable, and the screen says why.
 */

export function EscalationTargetsSettings() {
  const queryClient = useQueryClient();
  const [includeRetired, setIncludeRetired] = useState(false);
  const [level, setLevel] = useState<EscalationConfigLevel>('policy');
  const [policyKind, setPolicyKind] = useState<EscalationPolicyKind>('finding_type');
  const [policyKey, setPolicyKey] = useState('');
  const [scopeType, setScopeType] = useState<EscalationConfigScopeType>('org');
  const [scopeId, setScopeId] = useState('');
  const [targetUserId, setTargetUserId] = useState('');
  const [targetRoleKey, setTargetRoleKey] = useState('');

  const configs = useQuery({
    queryKey: ['insights', 'escalation-targets', includeRetired],
    queryFn: () => escalation.targets({ includeRetired }),
  });

  const workers = useQuery({
    queryKey: ['directory', 'workers', 'for-escalation-targets'],
    queryFn: () => directory.workers(),
  });
  const roles = useQuery({ queryKey: ['users', 'roles'], queryFn: () => users.roles() });
  const programList = useQuery({
    queryKey: ['programs', 'for-escalation-targets'],
    queryFn: () => programsApi.list(),
  });
  const siteList = useQuery({
    queryKey: ['sites', 'for-escalation-targets'],
    queryFn: () => sitesApi.list(),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['insights', 'escalation-targets'] });
  };

  const create = useMutation({
    mutationFn: () =>
      escalation.createTarget({
        level,
        policyKind: level === 'policy' ? policyKind : null,
        policyKey: level === 'policy' ? policyKey.trim() : null,
        scopeType,
        scopeProgramId: scopeType === 'program' ? scopeId : null,
        scopeLocationId: scopeType === 'location' ? scopeId : null,
        // A FALLBACK NAMES A ROLE, NEVER A PERSON — a CHECK enforces it, and
        // the form does not offer the combination that would be refused.
        targetUserId: level === 'policy' && targetUserId !== '' ? targetUserId : null,
        targetRoleKey: targetRoleKey !== '' ? targetRoleKey : null,
      }),
    onSuccess: () => {
      setPolicyKey('');
      setTargetUserId('');
      setTargetRoleKey('');
      setScopeId('');
      invalidate();
    },
  });

  // PH-UX-004. Retiring an escalation target changes where findings GO — the
  // screen's own empty state already spells out the fallback, so the dialog
  // says the same thing rather than inventing a second account of it.
  const [confirmRetire, setConfirmRetire] = useState<{ id: string; sentence: string } | null>(null);

  const retire = useMutation({
    mutationFn: (configId: string) => escalation.retireTarget(configId),
    onSuccess: invalidate,
  });

  const people = (workers.data ?? []).filter((worker) => worker.userId);
  const rows = configs.data ?? [];
  const scopeReady = scopeType === 'org' || scopeId !== '';
  const namesSomebody =
    level === 'org_fallback' ? targetRoleKey !== '' : targetUserId !== '' || targetRoleKey !== '';
  const policyReady = level === 'org_fallback' || policyKey.trim() !== '';
  const submittable = scopeReady && namesSomebody && policyReady && !create.isPending;

  return (
    <article className="rn-col" style={{ gap: 'var(--space-16)' }}>
      <header className="rn-col" style={{ gap: 'var(--space-8)' }}>
        <h1>Escalation targets</h1>
        <p className="rn-muted">
          Who a finding escalates to when nobody has been assigned it. Phibian consults these first,
          then the finding’s own owner, then your org chart, then your fallback role — and stops at
          the first of those that names anybody. It never chooses between two equally valid people;
          it says so and waits for one of yours to decide.
        </p>
      </header>

      {/* ---- What is configured now -------------------------------------- */}
      <section className="rn-card">
        <h2 className="rn-card-title">Configured now</h2>
        <label>
          <input
            type="checkbox"
            checked={includeRetired}
            onChange={(event) => setIncludeRetired(event.target.checked)}
          />{' '}
          Also show retired targets
          <span className="rn-muted">
            {' '}
            — a retired target still explains escalations it already chose.
          </span>
        </label>

        {configs.isLoading ? (
          <SkeletonSet surface="drawer" rows={3} />
        ) : configs.isError ? (
          <LoadFailure
            error={configs.error}
            message="We could not load your escalation targets."
            onRetry={() => void configs.refetch()}
            refusedHeading="You cannot see this organization’s escalation targets"
          />
        ) : rows.length === 0 ? (
          <p className="rn-muted" data-escalation-targets-empty>
            No escalation target is configured. Findings will fall through to your org chart, and
            where that names nobody they will record “No escalation owner configured” and wait for
            somebody here.
          </p>
        ) : (
          <>
            {/* Outside the <ul>: the dialog renders a div, and a <ul> may only
                contain <li>. */}
            <ConfirmAction
              open={confirmRetire !== null}
              title="Retire this escalation target?"
              consequence={
                (confirmRetire !== null ? `Retiring “${confirmRetire.sentence}”. ` : '') +
                'Findings that would have gone to this target will fall through to your org ' +
                'chart instead. Where the org chart names nobody, they will record “No ' +
                'escalation owner configured” and wait for somebody here — they are not ' +
                'escalated to anyone in the meantime. Retiring is recorded with a date rather ' +
                'than deleting the row, so the history of what was configured is kept.'
              }
              confirmLabel="Retire target"
              pendingLabel="Retiring…"
              pending={retire.isPending}
              onCancel={() => setConfirmRetire(null)}
              onConfirm={() => {
                if (confirmRetire !== null) retire.mutate(confirmRetire.id);
              }}
            />
            <ul className="rn-list">
              {rows.map((config) => (
                <li key={config.id} data-escalation-target-row={config.level}>
                  <span className="rn-strong">{targetConfigSentence(config)}</span>{' '}
                  {config.retiredAt === null ? (
                    <button
                      type="button"
                      className="rn-btn-quiet"
                      disabled={retire.isPending}
                      onClick={() =>
                        setConfirmRetire({ id: config.id, sentence: targetConfigSentence(config) })
                      }
                    >
                      Retire
                    </button>
                  ) : (
                    <span className="rn-muted" data-escalation-target-retired>
                      Retired{' '}
                      <time dateTime={config.retiredAt}>
                        {new Date(config.retiredAt).toLocaleDateString()}
                      </time>{' '}
                      — kept so the escalations it chose stay explainable.
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
        {retire.isError ? (
          <LoadFailure
            error={retire.error}
            message="We could not retire that target. Nothing has changed."
            refusedHeading="You cannot change this organization’s escalation targets"
          />
        ) : null}
      </section>

      {/* ---- Add one ----------------------------------------------------- */}
      <section className="rn-card">
        <h2 className="rn-card-title">Add an escalation target</h2>

        <label>
          What this is for
          <select
            value={level}
            onChange={(event) => {
              const next = event.target.value as EscalationConfigLevel;
              setLevel(next);
              // A fallback names a ROLE. Clearing the person here is not
              // tidiness — it is the CHECK constraint, applied before somebody
              // fills in a field the server would refuse.
              if (next === 'org_fallback') setTargetUserId('');
            }}
          >
            <option value="policy">One finding type or detector</option>
            <option value="org_fallback">
              Your organization’s fallback — anything nothing else covers
            </option>
          </select>
        </label>

        {level === 'policy' ? (
          <>
            <label>
              Which kind
              <select
                value={policyKind}
                onChange={(event) => setPolicyKind(event.target.value as EscalationPolicyKind)}
              >
                <option value="finding_type">A finding type</option>
                <option value="detector">One detector</option>
              </select>
            </label>
            <label>
              Its key
              <input
                value={policyKey}
                onChange={(event) => setPolicyKey(event.target.value)}
                placeholder="documentation.past_due"
              />
            </label>
          </>
        ) : (
          <p className="rn-muted">
            A fallback names a role rather than a person, so that it keeps working when the person
            holding it changes. Phibian reads it as “whoever holds this role today”.
          </p>
        )}

        <label>
          Where it applies
          <select
            value={scopeType}
            onChange={(event) => {
              setScopeType(event.target.value as EscalationConfigScopeType);
              setScopeId('');
            }}
          >
            <option value="org">Everywhere in this organization</option>
            <option value="program">One program</option>
            <option value="location">One site</option>
          </select>
        </label>

        {scopeType === 'program' ? (
          <label>
            Which program
            <select value={scopeId} onChange={(event) => setScopeId(event.target.value)}>
              <option value="">Choose a program…</option>
              {(programList.data?.programs ?? []).map((program) => (
                <option key={program.id} value={program.id}>
                  {program.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {scopeType === 'location' ? (
          <label>
            Which site
            <select value={scopeId} onChange={(event) => setScopeId(event.target.value)}>
              <option value="">Choose a site…</option>
              {(siteList.data?.sites ?? []).map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {scopeType !== 'org' ? (
          <p className="rn-muted" data-escalation-tie-warning>
            A program and a site are equally specific. If a finding sits inside both, and each has
            its own target here, Phibian will report “More than one escalation owner applies”
            instead of choosing between them.
          </p>
        ) : null}

        {level === 'policy' ? (
          <label>
            Escalate to this person
            <select
              value={targetUserId}
              onChange={(event) => {
                setTargetUserId(event.target.value);
                if (event.target.value !== '') setTargetRoleKey('');
              }}
            >
              <option value="">Nobody in particular</option>
              {people.map((worker) => (
                <option key={worker.userId!} value={worker.userId!}>
                  {worker.displayName}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <label>
          {level === 'policy' ? 'Or to whoever holds this role' : 'Escalate to whoever holds'}
          <select
            value={targetRoleKey}
            onChange={(event) => {
              setTargetRoleKey(event.target.value);
              if (event.target.value !== '') setTargetUserId('');
            }}
          >
            <option value="">Choose a role…</option>
            {(roles.data ?? []).map((role) => (
              <option key={role.key} value={role.key}>
                {role.name}
              </option>
            ))}
          </select>
        </label>

        <Button onClick={() => create.mutate()} disabled={!submittable} variant="secondary">
          Add escalation target
        </Button>

        {create.isError ? (
          <LoadFailure
            error={create.error}
            message="We could not add that escalation target. Nothing has changed."
            refusedHeading="You cannot change this organization’s escalation targets"
          />
        ) : null}
      </section>
    </article>
  );
}
